/**
 * Markdown -> plain text helpers.
 *
 * Tip and blog bodies are authored as markdown, but several places can only take
 * plain text: admin list previews and social composers (Facebook and LinkedIn
 * have no markdown support, so syntax pasted into them shows up literally).
 * These helpers flatten markdown to text that reads correctly in all of them.
 */

const FENCED_CODE = /```[^\n]*\n?([\s\S]*?)```/g;
const IMAGE = /!\[[^\]]*\]\([^)]*\)/g;
const INLINE_LINK = /\[([^\]]*)\]\(\s*<?([^)\s>]*)>?(?:\s+"[^"]*")?\s*\)/g;

const HEADING = /^\s{0,3}#{1,6}\s+/;
const BLOCKQUOTE = /^\s{0,3}>\s?/;
const THEMATIC_BREAK = /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;
const UNORDERED_ITEM = /^(\s*)[-*+]\s+/;
const ORDERED_ITEM = /^(\s*)(\d+)[.)]\s+/;
const TASK_MARKER = /^\[[ xX]\]\s+/;
const TABLE_DIVIDER = /^\s*\|?(?:\s*:?-{2,}:?\s*\|)+\s*:?-{2,}:?\s*\|?\s*$/;

/** Removes emphasis, strikethrough, and inline-code markers from a single line. */
function stripInlineMarks(text: string): string {
  return text
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/\*\*\*([^*]+)\*\*\*/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/___([^_]+)___/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    // Only unwrap _italics_ at word boundaries so snake_case identifiers survive.
    .replace(/(^|[\s(])_([^_]+)_(?=[\s.,!?)]|$)/g, '$1$2')
    .replace(/`([^`]+)`/g, '$1');
}

/** True when a line looks like a GFM table row rather than prose containing a pipe. */
function isTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') || (trimmed.match(/\|/g) ?? []).length >= 2;
}

function flattenTableRow(line: string): string {
  return line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map(cell => cell.trim())
    .filter(Boolean)
    .join(' | ');
}

/**
 * Converts markdown to plain text, preserving paragraph breaks and list
 * structure. Suitable for pasting straight into a social composer.
 */
export function toPlainText(markdown: string): string {
  if (!markdown) return '';

  const lines = markdown
    .replace(FENCED_CODE, (_match, code: string) => `\n${String(code).trim()}\n`)
    .replace(IMAGE, '')
    .replace(INLINE_LINK, (_match, label: string, url: string) => {
      const text = label.trim();
      if (!url) return text;
      if (!text || text === url) return url;
      return `${text} (${url})`;
    })
    .split(/\r?\n/);

  const out: string[] = [];

  for (const raw of lines) {
    if (THEMATIC_BREAK.test(raw) || TABLE_DIVIDER.test(raw)) continue;

    let line = raw.replace(BLOCKQUOTE, '').replace(HEADING, '');

    const unordered = UNORDERED_ITEM.exec(line);
    const ordered = unordered ? null : ORDERED_ITEM.exec(line);

    if (unordered) {
      line = `${unordered[1]}• ${line.slice(unordered[0].length).replace(TASK_MARKER, '')}`;
    } else if (ordered) {
      line = `${ordered[1]}${ordered[2]}. ${line.slice(ordered[0].length)}`;
    } else if (isTableRow(line)) {
      line = flattenTableRow(line);
    }

    out.push(stripInlineMarks(line).replace(/\s+$/, ''));
  }

  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Collapses markdown to a single line of plain text, truncated to `max`
 * characters. Used for admin list previews.
 */
export function excerpt(markdown: string, max = 160): string {
  const text = toPlainText(markdown).replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Builds the copy-to-clipboard payload for a social post: title, plain-text
 * body, then the public URL on its own line so Facebook auto-links it.
 */
export function buildSocialPost(params: { title: string; markdown: string; url?: string | null }): string {
  return [params.title.trim(), toPlainText(params.markdown), params.url?.trim()]
    .filter(Boolean)
    .join('\n\n');
}
